export type CharacterId = string;

/**
 * Charakter, den ein Spieler in einem Spiel verkörpert. IDs sind frei
 * wählbar, müssen aber zwischen Client und Server identisch sein.
 *
 * Sobald echte Sprites verfügbar sind, einfach unter
 * `public/assets/characters/<id>.png` ablegen – der Phaser-Loader nimmt
 * sie automatisch. Bis dahin zeichnet der Client einen Platzhalter aus
 * `color`, `accent` und `style`.
 */
export interface Character {
  id: CharacterId;
  name: string;
  description: string;
  /** Vorschau-Farbe (Hex) für Lobby-Tiles und Platzhalter-Sprites */
  color: string;
  /** Sekundärfarbe (z. B. Ohren/Akzente) für Platzhalter */
  accent: string;
  style: CharacterStyle;
}

/**
 * Visuelle Variante des Platzhalter-Sprites. Beim Hinzufügen eines neuen
 * Styles bitte sowohl `BootScene.ensurePlaceholderTextures` (Phaser) als
 * auch `CharacterAvatar` (React-Lobby) ergänzen.
 */
export type CharacterStyle =
  | "human"
  | "stitch"
  | "lion"
  | "snowman"
  | "mouse"
  | "genie"
  | "spaceman";

/**
 * Katalog der verfügbaren Charaktere. Reihenfolge bestimmt die Anzeige
 * in der Lobby. Neue Charaktere einfach hier eintragen.
 */
export const CHARACTERS: readonly Character[] = [
  {
    id: "default",
    name: "Held",
    description: "Der mutige Standard-Held. Stark, schnell, immer einsatzbereit.",
    color: "#7c3aed",
    accent: "#c4b5fd",
    style: "human",
  },
  {
    id: "stitch",
    name: "Stitch",
    description: "Klein, blau und ziemlich frech. Ohana heißt Familie.",
    color: "#38bdf8",
    accent: "#1e3a8a",
    style: "stitch",
  },
  {
    id: "simba",
    name: "Simba",
    description: "Junger König der Löwen – schnell, wild und mutig.",
    color: "#f59e0b",
    accent: "#7c2d12",
    style: "lion",
  },
  {
    id: "olaf",
    name: "Olaf",
    description: "Schneemann mit Sommer-Sehnsucht. Tappt ungelenk, aber liebenswert.",
    color: "#f8fafc",
    accent: "#f97316",
    style: "snowman",
  },
  {
    id: "mickey",
    name: "Mickey",
    description: "Die fröhliche Maus mit den unverwechselbaren Ohren.",
    color: "#1a0a2e",
    accent: "#ef4444",
    style: "mouse",
  },
  {
    id: "genie",
    name: "Genie",
    description: "Blauer Wirbelwind aus der Lampe. Drei Wünsche frei!",
    color: "#3b82f6",
    accent: "#fbbf24",
    style: "genie",
  },
  {
    id: "buzz",
    name: "Buzz",
    description: "Space-Ranger auf Mission – auf in die Unendlichkeit und noch viel weiter!",
    color: "#e2e8f0",
    accent: "#22c55e",
    style: "spaceman",
  },
] as const;

export const DEFAULT_CHARACTER_ID: CharacterId = "default";

export function getCharacter(id: CharacterId | undefined): Character {
  return (
    CHARACTERS.find((c) => c.id === id) ??
    (CHARACTERS.find((c) => c.id === DEFAULT_CHARACTER_ID) as Character)
  );
}
